import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileText, ShieldCheck, ExternalLink } from "lucide-react";

const CATEGORIES = ["Tax Returns", "Investment Statements", "Retirement", "Estate", "General"];

function formatBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get("/documents").then((r) => setDocs(r.data.documents || [])).catch(() => {});

  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDownload = async (doc) => {
    try {
      const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Delete ${doc.filename}?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="page-documents" className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-3">— Document Vault</div>
        <h1 className="font-serif text-5xl text-[#0F292F] font-light tracking-tight">Your Documents</h1>
        <p className="mt-3 text-[#52525B] max-w-xl">
          Upload tax forms, statements, and identity documents. Files are stored encrypted and accessible only to you and your advisor.
        </p>
      </div>

      <div className="bg-[#F3F1EC] border border-black/10 p-6 flex flex-wrap items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <ShieldCheck size={20} className="text-[#8E6A2B] mt-1 flex-shrink-0" />
          <div>
            <div className="font-medium text-[#0F292F]">Prefer to send via Encyro?</div>
            <div className="text-sm text-[#52525B] mt-1">
              Files arrive directly in Harold's secure Encyro inbox. End-to-end encrypted, no portal interaction required.
            </div>
          </div>
        </div>
        <a
          href="https://www.encyro.com/helliotassociates@gmail.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="portal-encyro-btn"
          className="btn-secondary"
        >
          <ShieldCheck size={16} /> Open Encyro <ExternalLink size={14} />
        </a>
      </div>

      <div className="bg-white border border-black/10 p-6 flex flex-wrap items-center gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Category</div>
          <select
            data-testid="doc-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="he-input bg-transparent w-56"
          >
            {CATEGORIES.map((c) => (<option key={c}>{c}</option>))}
          </select>
        </div>
        <div className="flex-1 min-w-0" />
        <input
          ref={fileRef}
          type="file"
          data-testid="doc-file-input"
          onChange={onUpload}
          className="hidden"
        />
        <button
          data-testid="doc-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="btn-accent disabled:opacity-60"
        >
          <Upload size={16} /> {uploading ? "Uploading..." : "Upload Document"}
        </button>
      </div>

      <div className="bg-white border border-black/10 overflow-hidden">
        <table className="w-full" data-testid="doc-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#52525B] border-b border-black/10">
              <th className="px-6 py-4">File</th>
              <th className="px-6 py-4">Category</th>
              <th className="px-6 py-4">Size</th>
              <th className="px-6 py-4">Uploaded</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-16 text-center text-[#52525B]">No documents yet. Upload your first file above.</td></tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-b last:border-b-0 border-black/5 hover:bg-[#F3F1EC]/40">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <FileText size={16} className="text-[#8E6A2B]" />
                    <span className="font-medium text-[#0F292F]">{d.filename}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-[#52525B]">{d.category}</td>
                <td className="px-6 py-4 text-sm text-[#52525B]">{formatBytes(d.size)}</td>
                <td className="px-6 py-4 text-sm text-[#52525B]">{new Date(d.uploaded_at).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => onDownload(d)} className="text-[#0F292F] hover:text-[#8E6A2B] mr-4 inline-flex items-center gap-1 text-sm" data-testid={`doc-download-${d.id}`}>
                    <Download size={14} /> Download
                  </button>
                  <button onClick={() => onDelete(d)} className="text-red-700 hover:text-red-900 inline-flex items-center gap-1 text-sm" data-testid={`doc-delete-${d.id}`}>
                    <Trash2 size={14} /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
